import { FC, FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { Helmet } from "react-helmet-async";

const AdminLogin: FC = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      const from = (location.state as any)?.from || "/admin";
      navigate(from, { replace: true });
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        (typeof err?.response?.data === "object"
          ? Object.values(err.response.data).flat()?.[0]
          : null) ||
        "Invalid credentials or not a staff account.";
      setError(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070f1f] flex items-center justify-center px-4">
      <Helmet>
        <title>Admin Login | BCR LTD</title>
      </Helmet>
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0c1830] p-8 shadow-2xl">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-primary-400">
          BCR Admin
        </p>
        <h1 className="mt-3 text-2xl font-bold text-white">Sign in</h1>
        <p className="mt-2 text-sm text-white/50">
          Use your Django staff account to manage website content.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-white/50 mb-2">
              Username
            </label>
            <input
              type="text"
              required
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
              placeholder="admin"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-white/50 mb-2">
              Password
            </label>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <div className="rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 text-sm px-4 py-3">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full h-11 rounded-xl bcr-btn-primary text-sm font-semibold disabled:opacity-60"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
